import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import partnerApi from '../api';
import { usePartnerAuth } from '../auth';

function formatCompact(n) {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString('en-IN');
}

export default function Dashboard() {
  const { partner } = usePartnerAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  useEffect(() => {
    partnerApi.get('/analytics/summary')
      .then(({ data }) => setAnalytics(data))
      .catch(() => setAnalytics(null))
      .finally(() => setLoadingAnalytics(false));
  }, []);

  const crawlerStatus = partner?.crawler_status || 'not_started';
  const productsIndexed = partner?.products_indexed || 0;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-serif text-2xl text-ink">
          Welcome, {partner?.brand_name}
        </h1>
        <p className="mt-0.5 text-sm text-ink-secondary">{partner?.brand_domain}</p>
      </div>

      {/* Crawler status */}
      <CrawlerBanner status={crawlerStatus} productsIndexed={productsIndexed} />

      {/* Analytics stats */}
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatTile
          label="Total clicks"
          value={loadingAnalytics ? null : formatCompact(analytics?.total_clicks ?? 0)}
          loading={loadingAnalytics}
          note={analytics?.total_clicks === 0 ? 'No clicks recorded yet' : undefined}
        />
        <StatTile
          label="Clicks this week"
          value={loadingAnalytics ? null : formatCompact(analytics?.clicks_this_week ?? 0)}
          loading={loadingAnalytics}
        />
        <StatTile
          label="Products indexed"
          value={productsIndexed > 0 ? formatCompact(productsIndexed) : '—'}
          note={productsIndexed === 0 ? 'Indexing in progress' : undefined}
        />
      </div>

      {/* Top products */}
      {!loadingAnalytics && analytics?.top_products?.length > 0 && (
        <div className="mt-6">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-muted">
            Top products by clicks
          </h2>
          <div className="overflow-hidden rounded-xl border border-hairline bg-surface">
            <table className="w-full text-sm">
              <thead className="border-b border-hairline bg-surface-page">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-ink-muted">Product</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-ink-muted">Clicks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {analytics.top_products.map((p, i) => (
                  <tr key={p.mira_sku} className="hover:bg-surface-page">
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/products/${p.mira_sku}`}
                        className="font-medium text-ink hover:text-raspberry-600"
                      >
                        #{i + 1} · {p.mira_sku}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums text-ink">
                      {formatCompact(p.clicks)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {productsIndexed === 0 && crawlerStatus !== 'running' && (
        <div className="mt-8 rounded-xl border border-hairline bg-surface p-10 text-center">
          <p className="text-sm font-medium text-ink">Your products are on their way</p>
          <p className="mt-1 text-sm text-ink-muted max-w-sm mx-auto">
            Our crawler is indexing your catalogue from{' '}
            <span className="font-medium text-ink">{partner?.brand_domain}</span>.
            Products and click data usually appear within a few hours.
          </p>
        </div>
      )}

      {/* Click data empty state (has products, no clicks yet) */}
      {productsIndexed > 0 && !loadingAnalytics && analytics?.total_clicks === 0 && (
        <p className="mt-4 text-xs text-ink-muted">
          No click data yet — clicks appear within 24 hours of your first product going live on mahi.fashion.
        </p>
      )}
    </div>
  );
}

function CrawlerBanner({ status, productsIndexed }) {
  const configs = {
    not_started: { bg: 'bg-surface border-hairline', dot: 'bg-ink-muted', text: 'Crawler not yet started.' },
    queued:      { bg: 'bg-accent-50 border-accent', dot: 'bg-accent', text: 'Crawler queued — indexing shortly.' },
    running:     { bg: 'bg-accent-50 border-accent', dot: 'bg-accent animate-pulse', text: 'Crawler running — indexing your products…' },
    completed:   { bg: 'bg-surface border-hairline', dot: 'bg-status-good', text: `${productsIndexed} product${productsIndexed === 1 ? '' : 's'} indexed.` },
    failed:      { bg: 'bg-surface border-status-serious', dot: 'bg-status-critical', text: 'Crawler encountered an issue. Contact partners@mahi.fashion.' },
  };
  const cfg = configs[status] || configs.not_started;
  return (
    <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${cfg.bg}`}>
      <span className={`h-2 w-2 flex-shrink-0 rounded-full ${cfg.dot}`} />
      <p className="text-sm text-ink-secondary">{cfg.text}</p>
    </div>
  );
}

function StatTile({ label, value, note, loading }) {
  return (
    <div className="rounded-xl border border-hairline bg-surface p-5">
      <p className="text-xs text-ink-muted">{label}</p>
      {loading ? (
        <div className="mt-1 h-7 w-16 animate-pulse rounded bg-hairline" />
      ) : (
        <p className="mt-1 text-2xl font-semibold tabular-nums text-ink">{value ?? '—'}</p>
      )}
      {note && <p className="mt-0.5 text-xs text-ink-muted">{note}</p>}
    </div>
  );
}
